import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { api } from '@/lib/api';
import * as Icons from 'lucide-react';

export default function PortalPage() {
  const { token } = useParams();

  const portalQ = useQuery({
    queryKey: ['public-tracking', token],
    enabled: Boolean(token),
    queryFn: async () => {
      const r = await api(`/api/v1/public/tracking/${token}`, { token: null });
      if (r.status === 404) {
        const err = new Error('missing');
        err.code = 'missing';
        throw err;
      }
      if (r.status !== 200) {
        const err = new Error('missing');
        err.code = 'missing';
        throw err;
      }
      return r.data;
    },
    retry: false,
  });

  const data = portalQ.data;
  const timeline = useMemo(() => {
    if (!data) return [];
    const status = data.status ?? 'unknown';
    return [
      {
        label: 'Pickup Window',
        detail: data.pickupDate ? new Date(data.pickupDate).toLocaleString() : '—',
        status: 'done',
      },
      {
        label: status.replace(/_/g, ' '),
        detail: `Load #${data.loadNumber ?? '—'}`,
        status:
          status === 'in_transit' || status === 'dispatched'
            ? 'live'
            : status === 'delivered' || status === 'closed'
              ? 'done'
              : 'pending',
      },
      {
        label: 'Delivery Window',
        detail: data.deliveryDate ? new Date(data.deliveryDate).toLocaleString() : '—',
        status: status === 'delivered' || status === 'closed' ? 'done' : 'pending',
      },
    ];
  }, [data]);

  if (portalQ.isLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (portalQ.isError || !data) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              LogisticSlash
            </p>
            <CardTitle className="text-2xl">Link not found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              This tracking link is invalid or no longer available.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                LogisticSlash · Customer Portal
              </p>
              <CardTitle className="text-2xl">Load Status</CardTitle>
              <p className="text-lg font-mono mt-1">#{data.loadNumber}</p>
            </div>
            {data.status ? <StatusPill status={data.status} showDot /> : null}
          </div>
        </CardHeader>
        <CardContent className="p-8">
          <div className="space-y-6">
            {timeline.map((step, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    step.status === 'done'
                      ? 'bg-green-500 text-white'
                      : step.status === 'live'
                        ? 'bg-primary text-white'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step.status === 'done' ? (
                    <Icons.Check className="h-5 w-5" />
                  ) : step.status === 'live' ? (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                    </span>
                  ) : (
                    <Icons.Circle className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1 pt-1">
                  <p className="font-medium capitalize">{step.label}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
